import { Component, OnInit } from '@angular/core';
import { getTenantLetterAssets } from '../../../_shared/utils/letter-assets';
import { getTenantConfig } from '../../../_shared/utils/tenant-context';
import {
  Positions,
  RequestList,
  SingleRequest,
  StatusButtons,
} from "../../../_shared/interfaces";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  RequestsStoreService,
  StaffStoreService,
  AlertService,
  IAlertType,
  FileStoreService, ProjectsStoreService
} from "../../../_shared/services";
import { map } from 'rxjs/operators';
import moment from "moment";

import PackageJson from '../../../../../package.json';
import { FormBuilder, FormGroup } from "@angular/forms";

import { CurrencyPipe, DatePipe, NgForOf, NgIf } from "@angular/common";
import { ClrDatagridModule, ClrDropdownModule, ClrModalModule, ClrTabsModule, ClrTooltipModule } from "@clr/angular";
import { RequestEditPositionComponent } from "./request-edit-position/request-edit-position.component";
import { RequestDetailsDocumentsComponent } from "./request-details-documents/request-details-documents.component";

import { environment as env } from "../../../../environments/environment";
import { ClarityIcons, angleIcon, eyeHideIcon, fileIcon, pencilIcon, minusCircleIcon, noteIcon } from "@cds/core/icon";
import { CommentFeatureCardComponent } from "../../../core/comment/comment-feature-card/comment-feature-card.component";

@Component({
  selector: 'app-request-details',
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    DatePipe,
    CurrencyPipe,
    ClrDropdownModule,
    ClrTabsModule,
    ClrDatagridModule,
    ClrTooltipModule,
    ClrModalModule,
    RequestEditPositionComponent,
    RequestDetailsDocumentsComponent,
    NgForOf,
    CommentFeatureCardComponent,
  ],
  templateUrl: './request-details.component.html',
  styleUrl: './request-details.component.sass'
})
export class RequestDetailsComponent implements OnInit {
  requests: SingleRequest = new SingleRequest();
  positions: RequestList[] = [];
  foundPositions: Positions[] = [];
  projectId: string = '';
  requestId: number = 0;
  positionId: number = 0;
  partId: number = 0;
  requestTotal: number = 0;
  loading1: boolean = true;
  loading2: boolean = true ;
  selected: RequestList[] = [];
  editPos: number = 0;  // 0 = Anzeige; 1 = Änderungsmodus, 2 = Position ändern
  statusLabel = 'label-light-blue';
  statusText = '';
  staffFirstName: string = '';
  staffLastName: string = '';
  staffEMail: string = '';
  staffPhone: string = '';
  commentsForm: FormGroup = new FormGroup({});
  devMode: boolean;

  public ppsVersion: string = PackageJson.version;
  private readonly tenantConfig = getTenantConfig();

  statusButtons: StatusButtons[] = [
    { state: 'open', display: 'Entwurf', label: 'label-light-blue', icon: 'info-circle', locked: false},
    { state: 'running', display: 'Abgeschickt', label: 'label-warning', icon: 'exclamation-triangle', locked: false},
    { state: 'rejected', display: 'Abgelehnt', label: 'label-danger', icon: 'exclamation-circle', locked: true},
    { state: 'too expensive', display: 'Zu teuer', label: 'label-info', icon: 'exclamation-circle', locked: true},
    { state: 'ignored', display: 'Ignoriert', label: 'label-warning', icon: 'exclamation-triangle', locked: true},
    { state: 'answered', display: 'Beantwortet', label: 'label-info', icon: 'check-circle', locked: true},
    { state: 'favored', display: 'Auftrag', label: 'label-success', icon: 'check-circle', locked: true},
  ];

  // Für Ausdruck
  basic: boolean = false;
  clrModalSize: string = '';

  documentDefinition = getTenantLetterAssets().a4Letter;

  constructor(
    private reqss: RequestsStoreService,
    private stss: StaffStoreService,
    private prss: ProjectsStoreService,
    private fss: FileStoreService,
    private router: Router,
    private alertDispatcher: AlertService,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.devMode = env.stages.dev;
    ClarityIcons.addIcons(angleIcon, fileIcon, eyeHideIcon, pencilIcon, minusCircleIcon, noteIcon)
  }

  ngOnInit() {
    const params = this.route.snapshot.paramMap;
    this.projectId = params.get('prid') ?? '';
    this.requestId = parseInt(params.get('reqid') ?? '0', 10);
    this.loading1 = true;
    this.loading2 = true;
    this.editPos = 0;
    this.clrModalSize = 'xl';
    this.loadRequest(this.projectId, this.requestId);
    this.loadRequestsPositions(this.projectId, this.requestId);
    this.searchProjectPositions(this.projectId);
  }

  private initForm() {
    this.commentsForm = this.fb.group(
      { comment: [this.requests.comment] }
    );
  }

  submit() {
    this.reqss.updateComment(this.requestId, this.projectId, this.commentsForm.value)
      .subscribe({
        next: data => {
          if (!data) {
            this.alertDispatcher.show({
              message: 'Keine Kommentardaten gefunden!',
              clrAlertType: IAlertType.WARNING
            }, 5000);
            return;
          }
          this.loadRequest(this.projectId, this.requestId);
        },
        complete: () => {
          this.alertDispatcher.show({
            message: 'Bemerkungen zum Request wurden geändert.',
            clrAlertType: IAlertType.INFO
          }, 5000);}
      });
  }

  loadRequest(prid: string, reqid: number) {
    this.reqss.getRequests(prid, reqid, 'all').subscribe({
      next: data1 => {
        if (!data1 || !data1.object) {
          this.alertDispatcher.show({
            message: 'Keine Requestdaten gefunden!',
            clrAlertType: IAlertType.WARNING
          }, 5000);
          return;
        }
        this.requests = data1.object[0];
      },
      complete: () => {
        if (!this.requests) {
          this.alertDispatcher.show({
            message: ' Request-Daten nicht verfügbar. ',
            clrAlertType: IAlertType.DANGER
          }, 2000);
          return;
        }
        // Mitarbeiter personalisieren
        this.staffStatus(this.requests.employee_phm);

        // Status gestalten
        const statusline = this.findButtonStatus(this.requests.status, this.statusButtons);
        this.statusLabel = this.statusButtons[statusline].label;
        this.statusText = this.statusButtons[statusline].display;

        this.initForm();
        this.loading1 = false;
      }
    });
  }

  loadRequestsPositions(prid: string, reqid: number) {
    let factor: number;
    this.requestTotal = 0;
    this.reqss.getRequestsPositions(prid, reqid, '0', 0, "all").subscribe({
      next: data2 => {
        if (!data2 || !data2.object) {
          this.alertDispatcher.show({
            message: 'Keine Requestpositionen gefunden!',
            clrAlertType: IAlertType.WARNING
          }, 5000);
          return;
        }
        this.positions = data2.object;
        this.positions.forEach((item) => {
          if (item.number_of_pieces == 0) { factor = 1 } else { factor = item.number_of_pieces }
          this.requestTotal += factor * item.purchasing_price;
        })
        this.updateTotal(prid, reqid, this.requestTotal);
      },
      complete: () => {
        this.loading2 = false;
      }
    });
  }

  async updateTotal(prid: string, reqid: number, sum: number) {
    if (reqid <= 0) {
      if (this.devMode) console.log('Skip request total update without persisted request_id');
      return;
    }

    this.reqss.updateRequestTotal(prid, reqid, sum).subscribe({
      next: data => {
        if (!data) {
          this.alertDispatcher.show({
            message: 'Beechnung der Gesamtsumme nicht möglich!',
            clrAlertType: IAlertType.WARNING
          }, 5000);
          return;
        } else {
          this.alertDispatcher.show({
            message: 'Gesamtsumme wurde aktualisiert.',
            clrAlertType: IAlertType.INFO
          }, 5000);
        }
      },
      complete: () => {
        if (this.devMode) console.log("Summe ist: " + this.requestTotal);
      }
    });
  }

  searchProjectPositions(projectId: string){
    this.prss.getPositions(projectId,0,0).pipe(
      map(res => (res?.object ?? []).filter(p => p.active === true))
    ).subscribe({
      next: (activePositions) => {
        if (activePositions.length === 0) {
          this.alertDispatcher.show({
            message: ' Keine aktiven Positionen gefunden. ',
            clrAlertType: IAlertType.WARNING
          }, 2000);
          return;
        }
        this.foundPositions = activePositions;
      },
      error: () => {
        return;
      }
    });
  }

  addRow(position: number, part: number | undefined, technicalDrawing: string, positionName: string) {
    if (confirm('Wollen Sie die Position ' + position + '.' + part + ' ' + positionName + ' (' + technicalDrawing + ') der Anfrage wieder hinzufügen?')) {
      this.reqss.addSingleProjectPosition(this.projectId, this.requestId, position, part || 0)
        .subscribe({
          next: (res: any) => {
            this.loading2 = false;

            // Immer neu laden. Die Abfrage stand vorher auf `res?.message`,
            // und die traegt das RMS auch im Erfolgsfall
            // ("request_position copied with success") -- die Liste blieb
            // dadurch stehen, obwohl die Position angelegt war, und der
            // Erfolg erschien als gelbe Warnung.
            this.loadRequestsPositions(this.projectId, this.requestId);

            // `object: false` heisst: es wurde nichts angelegt, die Position
            // gab es schon.
            if (res?.object === false) {
              this.alertDispatcher.show(
                { message: res.message ?? 'Die Position ist bereits in der Anfrage.', clrAlertType: IAlertType.WARNING },
                4000
              );
            }
          },
          error: () => {
            this.loading2 = false;
          }
        });
    }
  }

  staffStatus(staffID: number){
    this.stss.getStaffById(staffID)
      .subscribe(staff => {
        if (!staff) {
          this.staffFirstName = "";
          this.staffLastName = "---";
          this.staffEMail = "";
          this.staffPhone = "";
          return;
        }
        this.staffFirstName = staff.object[0].firstname;
        this.staffLastName = staff.object[0].lastname;
        this.staffEMail = staff.object[0].email;
        this.staffPhone = staff.object[0].phone;
      })
  }

  deleteRequest(projectID: string, requestID: number) {
    if (confirm('Wollen Sie die Anfrage wirklich löschen?\nDiese Aktion kann nicht rückgängig gemacht werden!')) {
      this.reqss.deleteRequest(projectID, requestID)
        .subscribe(data => this.router.navigate(['/project/details/', this.splitProjectNumber(projectID, 0), this.splitProjectNumber(projectID, 1)]));
    }
  }

  updatePositionMode(index: number, positionID: number, partID: number) {
    this.editPos = 2;
    this.positionId = positionID;
    this.partId = partID;
  }

  async updateRequest(done: boolean) {
    this.editPos = 1;
    await this.loadRequest(this.projectId, this.requestId);
    await this.loadRequestsPositions(this.projectId, this.requestId);
  }

  changeRequestStatus(projectID: string, requestID: number, status: number){
    if (confirm('Wollen Sie den Status dieser Anfrage wirklich auf "' + this.statusButtons[status].display + '" setzen?')) {
      switch (this.statusButtons[status].state) {
        case 'favored': {
          if (confirm('Wollen Sie für diese Anfrage gleich einen Auftrag anlegen?')) {
            /*this.ordss.createOrder(projectID, requestID)
              .subscribe(data => {
                this.reqss.updateRequestStatus(projectID, requestID, this.statusButtons[status].state)
                  .subscribe(datax => {
                    this.router.navigate(['/order/details/', projectID, data['message']]);
                  })
              });*/
          } else {
            this.reqss.updateRequestStatus(projectID, requestID, this.statusButtons[status].state)
              .subscribe(data => {
                this.loadRequest(projectID, requestID);
                this.loadRequestsPositions(projectID, requestID);
                this.router.navigate(['/request/details/', projectID, requestID]);
              })
          }
          break;
        }
        default: {
          this.reqss.updateRequestStatus(projectID, requestID, this.statusButtons[status].state)
            .subscribe(data => {
              this.loadRequest(projectID, requestID);
              this.loadRequestsPositions(projectID, requestID);
              this.router.navigate(['/request/details/', projectID, requestID]);
            });
          break;
        }
      }
    }
  }

  deletePosition(projectId: string, requestId: number, positionId: number, partId: number) {
    this.reqss.deleteRequestsPosition(projectId, requestId, positionId, partId)
      .subscribe({
        next: () => {
        this.loadRequestsPositions(projectId, requestId);
        },
        complete: () => {
          this.alertDispatcher.show({
            message: ' Position ' + positionId + '.' + partId + ' wurde gelöscht. ',
            clrAlertType: IAlertType.INFO
          }, 2000);
        },
        error: () => {
          return
        }
      });
  }

  // Anpassung Farbe und Text des Statusbuttons...
  findButtonStatus(projectstatus: string, buttons: StatusButtons[]) {
    return buttons.findIndex(el => el.state === projectstatus)
  };

  splitProjectNumber(projectid: string, substring: number) {
    var splits = projectid.split('.');
    return splits[substring];
  }

  createRequest(positions: RequestList[]) {
    let posTable = [[{ text: 'Pos.'}, { text: 'Bezeichnung'}, { text: 'Zeichnung', colSpan: 1, style: '' }, { text: 'Material'}, { text: 'Anzahl'}]];

    positions.forEach(position => {
      if (position.number_of_pieces == 0) position.number_of_pieces = 0;
      if (position.specification == 'null') position.specification = '';
      if (position.remarks == 'null') position.remarks = '';
      if (position.for_pdf) {
        if (position.description) position.name += '\n(' + position.description + ')';
        if (position.taric && position.taric !== "0") position.name += '\n(Zolltarifnummer: ' + position.taric + ')';
        switch (position.part) {
          case 0: {
            if (position.technology) {
              posTable.push(
                [{ text: position.position.toString(), style: 'positionText' } as any, { text: position.name }, { text: position.technical_drawing }, { text: position.material ?? '' }, { text: ''} ]
              );
            } else {
              posTable.push(
                [{ text: position.position.toString(), style: 'positionText' } as any, { text: position.name }, { text: position.technical_drawing }, { text: position.material ?? '' }, { text: (position.number_of_pieces).toString(), alignment: 'right' }]
              );
            }
            break;
          }
          default: {
            if (position.technology) {
              posTable.push(
                [{ text: position.position + '.' + position.part, style: 'positionText' } as any, { text: position.name }, { text: position.technical_drawing }, { text: position.material ?? '' }, { text: ''} ]
              );
            } else {
              posTable.push(
                [{ text: position.position + '.' + position.part, style: 'positionText' } as any, { text: position.name }, { text: position.technical_drawing }, { text: position.material ?? '' }, { text: (position.number_of_pieces).toString(), alignment: 'right'  }]
              );
            }
            break;
          }
        }
        if (position.remarks && (position.remarks != "null")) {
          posTable.push(
            [{ text: "" }, { text: "" }, {
              text: position.remarks,
              colSpan: 2,
              style: 'table'
            }, { text: "" }, { text: "" }]
          )
        }
      }
    });



    this.documentDefinition.info.title = 'PPS Anfrage';
    this.documentDefinition.info.subject = 'ANFRAGE ' + this.requests.project_id + '.0001.' + this.requests.request_id;
    this.documentDefinition.info.revison = this.ppsVersion;


    (this.documentDefinition.content[0] as any).columns[0].text = this.requests.requests_company + '\n' + this.requests.reqstreet + '\n' + this.requests.reqpostcode + ' ' + this.requests.reqcity;
    if(this.requests.reqcountry && (this.requests.reqcountry != 'Deutschland')) {
      (this.documentDefinition.content[0] as any).columns[0].text += '\n' + this.requests.reqcountry
    }
    (this.documentDefinition.content[0] as any).columns[2].text = this.tenantConfig.letterCity + ', ' + moment(this.requests.requests_date).format("DD.MM.yyyy") + '\n\nIhr Ansprechpartner:\n' + this.staffFirstName + ' ' + this.staffLastName + '\n' + this.staffEMail + '\n' + this.staffPhone;
    this.documentDefinition.content[1].text = 'ANFRAGE ' + this.requests.project_id + '.0001.' + this.requests.request_id ;
    if(this.requests.requests_person === 0) {
      this.documentDefinition.content[3].text = 'Sehr geehrte Damen und Herren,\n\n' +
        'wir bitten um ein verbindliches und für uns kostenfreies Angebot für:';
    } else {
      this.documentDefinition.content[3].text = 'Sehr geehrte(r) ' + this.requests.requests_salutation + ' ' + this.requests.requests_firstname + ' ' + this.requests.requests_lastname + ',\n\n' +
        'wir bitten um ein verbindliches und für uns kostenfreies Angebot für:';
    }
    (this.documentDefinition.content[4] as any).table.headerRows = 1;
    (this.documentDefinition.content[4] as any).table.widths = [ 'auto', '*', 'auto', 'auto', 'auto' ];
    (this.documentDefinition.content[4] as any).table.body = posTable;
    if (this.requests.requests_submit !== null) {
      this.documentDefinition.content[5] = {
        stack: [
          {
            text: 'Alle Teile sind entsprechend der Bestellung positionsweise zu kennzeichnen.\n\n' +
              'Für eine umgehende Angebotsabgabe bis zum ' + moment(this.requests.requests_submit).format("DD.MM.yyyy") + ' mit äußerster Preisstellung (Positionsweise Bestellung muss im Auftragsfall möglich sein) und Angabe der Lieferfristen bedanken wir uns im Voraus.', style: 'normal', margin: [30, 0]
          }
        ]
      } as any;
    } else {
      this.documentDefinition.content[5] = {
        stack: [
          {
            text: 'Alle Teile sind entsprechend der Bestellung positionsweise zu kennzeichnen.\n\n' +
              'Für eine umgehende Angebotsabgabe mit äußerster Preisstellung (Positionsweise Bestellung muss im Auftragsfall möglich sein) und Angabe der Lieferfristen bedanken wir uns im Voraus.', style: 'normal', margin: [30, 0]
          }
        ]
      } as any;
    }

   this.documentDefinition.content[6] = {
      stack: [
        {
          text: 'Mit freundlichen Grüßen aus ' + this.tenantConfig.letterCity + '\n' + this.tenantConfig.legalCompanyName, style: 'normal', margin: [30, 20]
        }
      ]
    } as any;

    this.documentDefinition.content[7] = {
      stack: [
        {
          id: 'subline',
          text: 'Dieses Schreiben wurde maschinell erstellt und ist ohne Unterschrift gültig.', style: 'small', margin: [30, 20],
          pageBreak: 'none'
        }
      ]
    } as any;
    this.documentDefinition.content.splice(8);

    this.generatePdf();
  }

  async setVisibility(position: number, part: number, usePDF: boolean) {
    let pdfValue = {
      "for_pdf": usePDF,
      "request_id": this.requestId,
      "part": part,
      "position": position,
      "project_id": this.projectId
    }

    this.reqss.updatePDFValue(pdfValue)
      .subscribe(() => {
        this.loadRequestsPositions(this.projectId, this.requestId);
      });
  }

  generatePdf() {
    const documentGenerator = pdfMake.createPdf(this.documentDefinition as any);

    documentGenerator.getDataUrl((dataUrl: string) => {
      const targetElement = document.querySelector('#documentContainer');
      targetElement!.innerHTML = '';

      if (!targetElement) {
        this.alertDispatcher.alertConfig.message = 'PDF-Container nicht gefunden!';
        this.alertDispatcher.alertConfig.clrAlertType = IAlertType.WARNING;
        return; // Exit the function if the element doesn't exist
      }

      const pdfIframe = document.createElement('iframe');
      pdfIframe.src = dataUrl;
      pdfIframe.width = '100%';
      pdfIframe.height = '100%';
      targetElement.appendChild(pdfIframe);
    })
  }


  archivePdf() {
    if (confirm('Wollen Sie die Datei ' + 'Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf archivieren?')) {
      this.safePdf();
    }

    this.alertDispatcher.show({
      message: ' Datei ' + 'Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf wurde archiviert.',
      clrAlertType: IAlertType.SUCCESS
    }, 5000);
  }

  safePdf() {
    const documentGenerator = pdfMake.createPdf(this.documentDefinition as any);
    documentGenerator.getBlob((blob: Blob) => {
      // Convert Blob to File
      const file = new File(
        [blob], // Blob content
        'Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf',
        { type: blob.type, lastModified: Date.now() } // File metadata
      );

      // Now, send the file instead of the Blob
      this.fss.sendFile(file, 10, this.requests.request_id, this.requests.project_id, 0, '', 'Anfrage ' + this.requests.project_id + '.0001.' + this.requests.request_id)
        .subscribe({
          next: (data) =>
          {
            if (this.devMode) console.log("done")
          },
          complete: () => {
            this.alertDispatcher.show({
              message: ' Datei ' + 'Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf wurde gespeichert.',
              clrAlertType: IAlertType.SUCCESS
            }, 5000);
          },
          error: (error) => console.error("Error:", error)
        });
    });
  }

  onDelete() {
    let choosenPositions = this.selected;
    if (confirm('Wollen Sie die ausgewählten Positionen tatsächlich löschen?')) {
      choosenPositions.forEach(array => {
        if (this.devMode) console.log("Ich lösche " + array.position + '.' + array.part);
        this.deletePosition(this.projectId, this.requestId, array.position, array.part)
      });

      this.loadRequest(this.projectId, this.requestId);
      this.loadRequestsPositions(this.projectId, this.requestId);

      this.alertDispatcher.show({
        message: ' Die Positionen wurden gelöscht. ',
        clrAlertType: IAlertType.SUCCESS
      }, 5000);
    }
  }

  async downloadPdf(){
    if (confirm('Wollen Sie die Datei ' + 'Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf wirklich herunterladen?')) {
      const documentGenerator = pdfMake.createPdf(this.documentDefinition).download('Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf');
    }

    await this.safePdf();

    this.alertDispatcher.show({
      message: ' Datei ' + 'Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf wurde herunterladen. ',
      clrAlertType: IAlertType.INFO
    }, 2000);
  }

  async printPdf() {
    if (confirm('Wollen Sie die Datei ' + 'Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf ausdrucken?')) {
      pdfMake.createPdf(this.documentDefinition).print();
    }

    await this.safePdf()

    this.alertDispatcher.show({
      message: ' Datei ' + 'Request_' + this.requests.project_id + '.0001.' + this.requests.request_id + '.pdf wurde an den Drucker übergeben. ',
      clrAlertType: IAlertType.INFO
    }, 2000);
  }
}
